// MACHINERY V2 (Stroje) — Diely a materiál: checkbox, thumbnail, quantity pair
// (M2.5).
//
// Spec: data package §5. Reference impl:
// design_handoff_machinery_v3/machinery-profile-parts.jsx (`PcCheck`,
// `PcThumb`); artboards 1009 / 1011 / 1013.
//
// THE " / " SEPARATOR ALWAYS RENDERS — EVEN AT ZERO. §5, twice: "Separator ' / '
// ALWAYS renders (limit object always truthy)" and, in the designer footer,
// "„ / " oddeľovač sa vykresľuje vždy (aj pri 0)". v1 gets this incidentally,
// because `LimitedQuantityFormat` tests `!!limit` and `inStockQuantity` is a
// non-optional object; here it is unconditional, so a future nullable
// `inStockQuantity` cannot silently delete the separator. The stock side turns
// amber at zero (artboard 1009, the HGJ-250-3 row).
//
// UNITS COME FROM THE SHARED MAP. `unitCode()` (common/models/CommonModels.ts —
// a file with NO imports at all, so it is safe for the v2 import graph) turns
// the enum into the `common:measurementUnits.*` key, exactly as
// QuantityFormat.tsx does.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { unitCode } from "../../../../../common/models/CommonModels";
// TYPE-ONLY on purpose: shared/models/models.ts imports @mui/material on its
// first line, so an elided type import is the only safe reference (§3).
import type { Quantity } from "../../../../../shared/models/models";

export const PartCheckbox: React.FC<{
  checked: boolean;
  indeterminate?: boolean;
  label: string;
  onChange: () => void;
}> = ({ checked, indeterminate, label, onChange }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={indeterminate ? "mixed" : checked}
    aria-label={label}
    onClick={(e) => {
      e.stopPropagation();
      onChange();
    }}
    style={{
      width: 16,
      height: 16,
      borderRadius: 4,
      flexShrink: 0,
      boxSizing: "border-box",
      cursor: "pointer",
      padding: 0,
      border: "1.5px solid " + (checked || indeterminate ? "var(--mod-accent)" : "var(--border)"),
      background: checked || indeterminate ? "var(--mod-accent)" : "var(--card)",
      display: "grid",
      placeItems: "center",
    }}
  >
    {checked && !indeterminate && (
      <svg viewBox="0 0 10 10" width="9" height="9" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
        <path d="M2 5.2 L4.2 7.4 L8 3" />
      </svg>
    )}
    {indeterminate && <span style={{ width: 7, height: 1.6, background: "#fff", borderRadius: 1 }} />}
  </button>
);

export const PartThumb: React.FC<{ src?: string | null; size?: number }> = ({ src, size = 42 }) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <span
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 7,
        overflow: "hidden",
        background: "var(--bg-soft)",
        border: "1px solid var(--border-soft)",
        display: "grid",
        placeItems: "center",
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 7.5,
            letterSpacing: "0.06em",
            color: "var(--faint)",
            textTransform: "uppercase",
            textAlign: "center",
            lineHeight: 1.2,
          }}
        >
          {translate("myMachinery:v2.tabs.parts.noPhoto")}
        </span>
      )}
    </span>
  );
};

const QtyValue: React.FC<{ quantity?: Quantity | null; tone: string }> = ({ quantity, tone }) => {
  const { translate } = useTranslation("common");
  const unit = unitCode(quantity?.unitCode);
  return (
    <span style={{ color: tone }}>
      {quantity?.value}
      {unit ? " " + translate(`common:measurementUnits.${unit}`) : ""}
    </span>
  );
};

/** "installed / in stock" — see the header for why the separator is absolute. */
export const InstalledOverStock: React.FC<{
  installed?: Quantity | null;
  inStock?: Quantity | null;
}> = ({ installed, inStock }) => (
  <span
    style={{
      fontFamily: "var(--font-mono)",
      fontSize: 12,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
    }}
  >
    <b style={{ color: "var(--ink)", fontWeight: 700 }}>
      <QtyValue quantity={installed} tone="var(--ink)" />
    </b>
    <span style={{ color: "var(--faint)" }}> / </span>
    <QtyValue
      quantity={inStock}
      tone={
        inStock?.value === 0
          ? "color-mix(in oklab, var(--amber) 80%, var(--ink))"
          : "var(--muted)"
      }
    />
  </span>
);
